export default function (Alpine) {

    // ─────────────────────────────────────────────
    // Yardımcı: panoya yaz
    // Güvenli bağlam (HTTPS) varsa Clipboard API,
    // yoksa eski execCommand fallback'i kullanır.
    // ─────────────────────────────────────────────
    function writeToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        }
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch {}
        document.body.removeChild(ta);
        return Promise.resolve();
    }

    // ─────────────────────────────────────────────
    // Yardımcı: ID'ye göre hedef elementin metnini al
    // innerText → textContent → value sırasıyla dener
    // ─────────────────────────────────────────────
    function getTargetText(targetId) {
        const el = document.getElementById(targetId);
        if (!el) return '';
        return el.innerText || el.textContent || el.value || '';
    }

    // ═════════════════════════════════════════════
    // Component 1: copyText
    // ─────────────────────────────────────────────
    // Tıklandığında elementin KENDİ içeriğini kopyalar.
    // Hedef göstermeye gerek yoktur; element hem kaynak
    // hem tetikleyicidir.
    //
    // KULLANIM:
    //
    //   Temel:
    //     <code x-data="copyText">npm install alpinejs</code>
    //
    //   Farklı animasyon class'ı:
    //     <pre x-data="copyText({ class: 'copied' })">...</pre>
    //
    //   Farklı süre (ms):
    //     <span x-data="copyText({ duration: 3000 })">...</span>
    //
    //   İkisi birden:
    //     <code x-data="copyText({ class: 'copied', duration: 1500 })">
    //       const x = 1;
    //     </code>
    //
    // KISACA:
    //   Kopyalama başarılı olunca elementin üzerine
    //   copiedClass (default: 'is-copied') eklenir,
    //   copiedDuration (default: 2000ms) sonra kaldırılır.
    //
    // CSS ÖRNEĞİ:
    //   .is-copied { color: green; }
    //   .is-copied::after { content: ' ✓'; }
    // ═════════════════════════════════════════════
    Alpine.data('copyText', (options = {}) => ({
        copiedClass: options.class || 'is-copied',
        copiedDuration: options.duration || 2000,
        copied: false,
        _timer: null,

        init() {
            this.$root.addEventListener('click', () => this.copy());
        },

        copy() {
            const text = this.$root.innerText
                || this.$root.textContent
                || this.$root.value
                || '';

            writeToClipboard(text.trim()).then(() => {
                this._showCopied();
            });
        },

        _showCopied() {
            if (this._timer) clearTimeout(this._timer);
            this.copied = true;
            this.$root.classList.add(this.copiedClass);

            this._timer = setTimeout(() => {
                this.copied = false;
                this.$root.classList.remove(this.copiedClass);
                this._timer = null;
            }, this.copiedDuration);
        },

        destroy() {
            if (this._timer) clearTimeout(this._timer);
        },
    }));

    // ═════════════════════════════════════════════
    // Component 2: copyButton
    // ─────────────────────────────────────────────
    // Tıklandığında BAŞKA bir elementin içeriğini kopyalar.
    // Hedef, target attribute'u ile belirtilir.
    //
    // KULLANIM:
    //
    //   Temel (target="#id"):
    //     <p id="my-text">Kopyalanacak içerik</p>
    //     <button x-data="copyButton" target="#my-text">Kopyala</button>
    //
    //   data-target ile (# olmadan da olur):
    //     <button x-data="copyButton" data-target="my-text">Kopyala</button>
    //
    //   <pre> veya <code> bloğunu kopyalamak:
    //     <pre id="code-block">
    //       const a = 1;
    //       const b = 2;
    //     </pre>
    //     <button x-data="copyButton" target="#code-block">
    //       Kodu kopyala
    //     </button>
    //
    //   <input> veya <textarea> değerini kopyalamak:
    //     <input id="api-key" value="sk-abc123" readonly />
    //     <button x-data="copyButton" target="#api-key">
    //       Anahtarı kopyala
    //     </button>
    //
    //   Özel class ve süre:
    //     <button
    //       x-data="copyButton({ class: 'btn--copied', duration: 1500 })"
    //       target="#my-text"
    //     >
    //       Kopyala
    //     </button>
    //
    //   Otomatik tarama için btn-copy class'ı:
    //     <button class="btn-copy" target="#my-text">Kopyala</button>
    //     (x-data yazmana gerek yok, plugin otomatik ekler)
    //
    // KISACA:
    //   target veya data-target yoksa hiçbir şey yapmaz.
    //   Hedef element bulunamazsa (yanlış ID) sessizce çıkar.
    // ═════════════════════════════════════════════
    Alpine.data('copyButton', (options = {}) => ({
        copiedClass: options.class || 'is-copied',
        copiedDuration: options.duration || 2000,
        copied: false,
        _timer: null,

        init() {
            this.$root.addEventListener('click', () => this.copy());
        },

        copy() {
            const raw = this.$root.getAttribute('target')
                || this.$root.dataset.target
                || '';
            const targetId = raw.replace(/^#/, '');
            const text = getTargetText(targetId);

            if (!text) return;

            writeToClipboard(text.trim()).then(() => {
                this._showCopied();
            });
        },

        _showCopied() {
            if (this._timer) clearTimeout(this._timer);
            this.copied = true;
            this.$root.classList.add(this.copiedClass);

            this._timer = setTimeout(() => {
                this.copied = false;
                this.$root.classList.remove(this.copiedClass);
                this._timer = null;
            }, this.copiedDuration);
        },

        destroy() {
            if (this._timer) clearTimeout(this._timer);
        },
    }));

    // ─────────────────────────────────────────────
    // Otomatik tarama
    // btn-copy class'ı taşıyan elementlere otomatik
    // x-data ekler. target/data-target varsa copyButton,
    // yoksa copyText olarak başlatır.
    // ─────────────────────────────────────────────
    const copyTargets = ['btn-copy'];

    function setupCopyElement(el, index) {
        if (el.dataset.copyReady) return;
        el.dataset.copyReady = 'true';

        if (!el.style.getPropertyValue('--i')) {
            el.style.setProperty('--i', index);
        }

        const targetId = el.getAttribute('target')
            || el.dataset.target || '';
        const animClass = el.dataset.copiedClass || 'is-copied';
        const duration  = el.dataset.copiedDuration || 2000;

        if (targetId) {
            el.setAttribute('x-data',
                `copyButton({ class: '${animClass}', duration: ${duration} })`
            );
        } else {
            el.setAttribute('x-data',
                `copyText({ class: '${animClass}', duration: ${duration} })`
            );
        }

        Alpine.initTree(el);
    }

    function scanCopy() {
        const selector = copyTargets
            .map(c => `.${CSS.escape(c)}`).join(',');
        if (!selector) return;
        document.querySelectorAll(selector).forEach(setupCopyElement);
    }

    queueMicrotask(scanCopy);
    document.addEventListener('DOMContentLoaded', () => queueMicrotask(scanCopy));
    document.addEventListener('alpine:initialized', () => queueMicrotask(scanCopy));
}